import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { CalendarDays, Clock, Phone, MapPin, User, Mail, Loader2, CheckCircle, Wrench } from 'lucide-react';
import { apiRequest } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import SEOHead from '../components/SEOHead';

export default function AppointmentPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    
    const [services, setServices] = useState([]);
    const [timeSlots, setTimeSlots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [date, setDate] = useState(null);
    
    const [formData, setFormData] = useState({
        name: user?.name || '',
        email: user?.email || '',
        phone: '',
        service_type: '',
        description: '',
        preferred_time: '',
        address: ''
    });

    useEffect(() => {
        fetchServices();
        fetchTimeSlots();
    }, []);

    useEffect(() => {
        if (user) {
            setFormData(prev => ({
                ...prev,
                name: user.name || prev.name,
                email: user.email || prev.email
            }));
        }
    }, [user]);

    const fetchServices = async () => {
        try {
            const data = await apiRequest('/appointments/services');
            setServices(data);
        } catch (error) {
            console.error('Failed to fetch services:', error);
        }
    };

    const fetchTimeSlots = async () => {
        try {
            const data = await apiRequest('/appointments/timeslots');
            setTimeSlots(data);
        } catch (error) {
            console.error('Failed to fetch time slots:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name || !formData.email || !formData.phone || !formData.service_type || !date || !formData.preferred_time || !formData.address) {
            toast.error('Veuillez remplir tous les champs obligatoires');
            return;
        }

        setLoading(true);
        try {
            await apiRequest('/appointments', {
                method: 'POST',
                body: JSON.stringify({
                    ...formData,
                    preferred_date: format(date, 'yyyy-MM-dd')
                })
            });
            setSuccess(true);
            toast.success('Demande de rendez-vous envoyée !');
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="min-h-screen pt-20 pb-12 flex items-center justify-center" data-testid="appointment-success">
                <Card className="glass rounded-3xl max-w-md w-full mx-4">
                    <CardContent className="py-12 text-center">
                        <div className="h-20 w-20 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-6">
                            <CheckCircle className="h-10 w-10 text-emerald-500" />
                        </div>
                        <h1 className="font-heading text-2xl font-bold mb-2">Demande envoyée !</h1>
                        <p className="text-muted-foreground mb-6">
                            Nous avons bien reçu votre demande de rendez-vous. 
                            Notre équipe vous contactera rapidement pour confirmer le créneau.
                        </p>
                        <div className="space-y-3">
                            <Button className="w-full rounded-full" onClick={() => navigate('/')}>
                                Retour à l'accueil
                            </Button>
                            <Button variant="secondary" className="w-full rounded-full" onClick={() => setSuccess(false)}>
                                Nouveau rendez-vous
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <>
            <SEOHead 
                title="Rendez-vous Piscine - Recherche de Fuite, Réparation, Entretien | O.C.A Piscine Var"
                description="Prenez rendez-vous avec O.C.A Piscine à Flayosc (83). Recherche de fuite piscine, diagnostic, réparation pompe et filtration, entretien. Intervention rapide dans le Var."
                keywords="recherche fuite piscine, fuite piscine Var, réparation piscine, entretien piscine Flayosc, diagnostic piscine, technicien piscine 83"
            />
            <div className="min-h-screen pt-20 pb-12" data-testid="appointment-page">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <CalendarDays className="h-10 w-10 text-primary" />
                        <h1 className="font-heading text-3xl sm:text-4xl font-bold">Prendre rendez-vous</h1>
                    </div>
                    <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                        Nos techniciens experts interviennent pour tous vos besoins : <strong>recherche de fuite</strong>, diagnostic, réparation, entretien, installation...
                    </p>
                </div>

                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Services info */}
                    <div className="space-y-4">
                        <h2 className="font-heading text-xl font-semibold">Nos services</h2>
                        {services.map(service => (
                            <Card key={service.id} className={`glass rounded-2xl cursor-pointer transition-all ${formData.service_type === service.id ? 'ring-2 ring-primary' : ''}`}
                                onClick={() => setFormData(prev => ({ ...prev, service_type: service.id }))}>
                                <CardContent className="p-4">
                                    <div className="flex items-start gap-3">
                                        <Wrench className="h-5 w-5 text-primary mt-0.5" />
                                        <div>
                                            <h3 className="font-semibold">{service.name}</h3>
                                            <p className="text-sm text-muted-foreground">{service.description}</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>

                    {/* Form */}
                    <div className="lg:col-span-2">
                        <Card className="glass rounded-3xl">
                            <CardHeader>
                                <CardTitle>Formulaire de demande</CardTitle>
                                <CardDescription>Remplissez le formulaire et nous vous recontacterons</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <form onSubmit={handleSubmit} className="space-y-6">
                                    {/* Contact info */}
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="name">Nom complet *</Label>
                                            <div className="relative">
                                                <User className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                                <Input
                                                    id="name"
                                                    value={formData.name}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                                    placeholder="Votre nom"
                                                    className="pl-10 h-12 rounded-xl"
                                                    data-testid="appointment-name"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="email">Email *</Label>
                                            <div className="relative">
                                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                                <Input
                                                    id="email"
                                                    type="email"
                                                    value={formData.email}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                                                    placeholder="votre@email.com"
                                                    className="pl-10 h-12 rounded-xl"
                                                    data-testid="appointment-email"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="phone">Téléphone *</Label>
                                            <div className="relative">
                                                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                                <Input
                                                    id="phone"
                                                    value={formData.phone}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                                                    placeholder="06 12 34 56 78"
                                                    className="pl-10 h-12 rounded-xl"
                                                    data-testid="appointment-phone"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Type d'intervention *</Label>
                                            <Select value={formData.service_type} onValueChange={(value) => setFormData(prev => ({ ...prev, service_type: value }))}>
                                                <SelectTrigger className="h-12 rounded-xl" data-testid="appointment-service">
                                                    <SelectValue placeholder="Sélectionnez un service" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {services.map(service => (
                                                        <SelectItem key={service.id} value={service.id}>
                                                            {service.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>

                                    {/* Date and time */}
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Date souhaitée *</Label>
                                            <Popover>
                                                <PopoverTrigger asChild>
                                                    <Button variant="outline" className="w-full h-12 rounded-xl justify-start font-normal" data-testid="appointment-date">
                                                        <CalendarDays className="mr-2 h-4 w-4" />
                                                        {date ? format(date, 'PPP', { locale: fr }) : 'Choisir une date'}
                                                    </Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="w-auto p-0" align="start">
                                                    <Calendar
                                                        mode="single"
                                                        selected={date}
                                                        onSelect={setDate}
                                                        disabled={(date) => date < new Date() || date.getDay() === 0}
                                                        initialFocus
                                                    />
                                                </PopoverContent>
                                            </Popover>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Créneau horaire *</Label>
                                            <Select value={formData.preferred_time} onValueChange={(value) => setFormData(prev => ({ ...prev, preferred_time: value }))}>
                                                <SelectTrigger className="h-12 rounded-xl" data-testid="appointment-time">
                                                    <Clock className="mr-2 h-4 w-4" />
                                                    <SelectValue placeholder="Choisir un horaire" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {timeSlots.map(slot => (
                                                        <SelectItem key={slot} value={slot}>
                                                            {slot}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>

                                    {/* Address */}
                                    <div className="space-y-2">
                                        <Label htmlFor="address">Adresse d'intervention *</Label>
                                        <div className="relative">
                                            <MapPin className="absolute left-4 top-3 h-4 w-4 text-muted-foreground" />
                                            <Input
                                                id="address"
                                                value={formData.address}
                                                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                                                placeholder="123 Rue de la Piscine, 75001 Paris"
                                                className="pl-10 h-12 rounded-xl"
                                                data-testid="appointment-address"
                                            />
                                        </div>
                                    </div>

                                    {/* Description */}
                                    <div className="space-y-2">
                                        <Label htmlFor="description">Description du problème</Label>
                                        <Textarea
                                            id="description"
                                            value={formData.description}
                                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                            placeholder="Décrivez votre problème ou votre demande..."
                                            className="min-h-[100px] rounded-xl"
                                            data-testid="appointment-description"
                                        />
                                    </div>

                                    <Button
                                        type="submit"
                                        className="w-full rounded-full h-12 font-semibold"
                                        disabled={loading}
                                        data-testid="appointment-submit"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                                Envoi en cours...
                                            </>
                                        ) : (
                                            'Demander un rendez-vous'
                                        )}
                                    </Button>
                                </form>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
}
